/**
 * The SDK's error type and the one mapping from an HTTP refusal to it.
 *
 * Its own module so that `upload.ts` can SUBCLASS it (`UploadStalledError`,
 * `UploadTooLargeError` — F39/N17). While it lived in `base.ts`, which imports
 * `upload.ts`, a `class … extends TonnieApiError` in `upload.ts` evaluated
 * before `base.ts` had defined the parent and threw "Class extends value
 * undefined". `base.ts` re-exports everything here, so every existing import
 * path is unchanged.
 */

export interface ApiError {
  message: string;
  code?: string;
  status: number;
  details?: Record<string, unknown>;
  /** Domain-error payload — see `TonnieApiError.data` docstring. */
  data?: Record<string, unknown>;
}

/**
 * Machine code for an error whose body carried none — a non-JSON body, an empty
 * 502 from a proxy, a network-level failure. Preserves the codes the old
 * 401/403 short-circuits used to hardcode, for exactly the cases where there is
 * genuinely nothing better to report. When the server DID send a code, that
 * code wins; this is the floor, not the default.
 */
const STATUS_CODE_FALLBACK: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  402: 'PAYMENT_REQUIRED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  410: 'GONE',
  422: 'VALIDATION_ERROR',
  429: 'TOO_MANY_REQUESTS',
  500: 'INTERNAL_ERROR',
  502: 'BAD_GATEWAY',
  503: 'SERVICE_UNAVAILABLE',
};

export class TonnieApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: Record<string, unknown>;
  /**
   * Structured domain-error payload — e.g. `{ expectedRowVersion,
   * actualRowVersion, flowId }` on a `GuideStateConflictError` (409).
   *
   * Distinct from `details`, which historically carries Zod-style
   * validation issues (`Array<{ field, message, code }>`). Backend's
   * `HttpExceptionFilter` puts TonnieError payloads here so the SDK
   * can branch on them without ambiguity.
   */
  public readonly data?: Record<string, unknown>;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'TonnieApiError';
    this.status = error.status;
    this.code = error.code;
    this.details = error.details;
    this.data = error.data;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * A transport-level failure where the HTTP request never reached the
   * server in a way we could classify: CORS preflight blocked, DNS
   * failure, offline, TLS error, etc. The browser / RN engine throws a
   * generic `TypeError: Failed to fetch` in this case; we wrap it in
   * `executeRequest` so callers can branch on `error.isNetworkError`
   * instead of pattern-matching on the message.
   */
  get isNetworkError(): boolean {
    return this.status === 0 || this.code === 'NETWORK_ERROR';
  }

  /**
   * Timeout — we gave up waiting for the response (AbortController
   * fired). Status 408 is borrowed from the HTTP spec to keep the
   * shape consistent with 4xx errors, but this error never actually
   * reached a server.
   */
  get isTimeout(): boolean {
    return this.status === 408 || this.code === 'TIMEOUT';
  }
}

/**
 * Build a `TonnieApiError` from a non-2xx status plus whatever the body parsed
 * to (or `undefined` when it did not parse).
 *
 * Shared by the fetch path (`handleResponse`) and the XHR upload path
 * (`upload.ts`) so a 413 from the upload proxy carries the same machine code
 * and message a 413 from a JSON call would. Two copies of this mapping is how a
 * server-supplied code gets discarded on one transport and not the other.
 */
export function apiErrorFrom(status: number, statusText: string, body: unknown): TonnieApiError {
  const errorBody = (body ?? { message: statusText || 'An error occurred' }) as Record<
    string,
    unknown
  >;
  // Support nested API error format: { error: { code, message, details }, meta }
  const apiError = ((errorBody['error'] as Record<string, unknown> | undefined) ??
    errorBody) as Record<string, unknown>;

  return new TonnieApiError({
    message:
      (apiError['message'] as string) ||
      (errorBody['message'] as string) ||
      `HTTP Error: ${status}`,
    status,
    code:
      (apiError['code'] as string) ||
      (errorBody['code'] as string) ||
      STATUS_CODE_FALLBACK[status] ||
      `HTTP_${status}`,
    details: (apiError['details'] ?? errorBody['details'] ?? errorBody['errors']) as
      Record<string, unknown> | undefined,
    data: (apiError['data'] ?? errorBody['data']) as Record<string, unknown> | undefined,
  });
}
